import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Trash2, Users, MapPin, Mail, Calendar, Search, Send, ShieldCheck, ShieldAlert, Eye, Check, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const AdminPanel = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [viewIdImage, setViewIdImage] = useState(null);
  const [activeTab, setActiveTab] = useState('users');
  const navigate = useNavigate();
  const { user } = React.useContext(AuthContext);

  useEffect(() => {
    if (!user?.is_admin) {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchUsers();
    fetchPendingVerifications();
  }, [user, navigate]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = users.filter(u =>
        u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingVerifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/pending-verifications`, { headers: { Authorization: `Bearer ${token}` } });
      setPendingVerifications(response.data);
    } catch (error) {
      console.error('Failed to load pending verifications:', error);
    }
  };

  const handleDeleteUser = async (userId, userName, userEmail) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}" (${userEmail})?\n\nThis will also delete all their posts and messages. This action cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleEmailNoLocationUsers = async () => {
    const noLocCount = users.filter(u => !u.location).length;
    if (noLocCount === 0) { toast.info('All users have their location set!'); return; }
    if (!window.confirm(`Send an email to ${noLocCount} user(s) without a location?`)) return;
    setEmailSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/email-no-location-users`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(response.data.message);
      if (response.data.failed?.length > 0) toast.error(`Failed: ${response.data.failed.join(', ')}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send emails');
    } finally {
      setEmailSending(false);
    }
  };

  const handleVerifyUser = async (userId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/verify-user/${userId}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`User ${status === 'verified' ? 'approved' : 'rejected'} successfully`);
      fetchPendingVerifications();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update verification');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getVerificationBadge = (u) => {
    if (u.email === user?.email && user?.is_admin) return <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full">Verified (Admin)</span>;
    const status = u.verification_status || 'unverified';
    if (status === 'verified') return <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full">Verified</span>;
    if (status === 'pending') return <span className="px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full">Pending</span>;
    if (status === 'rejected') return <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">Rejected</span>;
    return <span className="px-2 py-0.5 bg-gray-400 text-white text-xs rounded-full">Unverified</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: 'rgb(26, 160, 157)' }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-emerald-50">
      {/* ID Image Viewer Dialog */}
      <Dialog open={!!viewIdImage} onOpenChange={() => setViewIdImage(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>ID Document</DialogTitle>
          </DialogHeader>
          {viewIdImage && (
            <img src={viewIdImage} alt="User ID" className="w-full rounded-lg" />
          )}
          <Button onClick={() => setViewIdImage(null)} className="w-full mt-2" variant="outline" data-testid="close-id-dialog-btn">
            Close
          </Button>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div
                style={{ backgroundColor: 'rgb(26, 160, 157)' }}
                className="px-6 py-2.5 rounded-lg shadow-md cursor-pointer"
                onClick={() => navigate('/dashboard')}
              >
                <h1 className="text-xl font-bold text-white logo-text whitespace-nowrap">STRAY SPOTTER</h1>
              </div>
            </div>
            <Button onClick={() => navigate('/dashboard')} variant="ghost" data-testid="back-btn" className="hover:bg-teal-50">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2 text-gray-900 flex items-center gap-3" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            <Users className="w-10 h-10" style={{ color: 'rgb(26, 160, 157)' }} />
            Admin Panel
          </h1>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            onClick={() => setActiveTab('users')}
            variant={activeTab === 'users' ? 'default' : 'outline'}
            data-testid="tab-users"
            style={activeTab === 'users' ? { backgroundColor: 'rgb(26, 160, 157)' } : {}}
          >
            <Users className="w-4 h-4 mr-2" /> Users ({users.length})
          </Button>
          <Button
            onClick={() => setActiveTab('verifications')}
            variant={activeTab === 'verifications' ? 'default' : 'outline'}
            data-testid="tab-verifications"
            style={activeTab === 'verifications' ? { backgroundColor: 'rgb(26, 160, 157)' } : {}}
          >
            <ShieldCheck className="w-4 h-4 mr-2" /> Pending Verifications ({pendingVerifications.length})
          </Button>
        </div>

        {activeTab === 'users' && (
          <>
            {/* Stats */}
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <Card className="shadow-lg border-0">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Total Users</p>
                      <p className="text-3xl font-bold" style={{ color: 'rgb(26, 160, 157)' }}>{users.length}</p>
                    </div>
                    <Users className="w-12 h-12 opacity-20" style={{ color: 'rgb(26, 160, 157)' }} />
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-lg border-0">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Verified</p>
                      <p className="text-3xl font-bold text-green-600">
                        {users.filter(u => u.verification_status === 'verified' || u.email === user?.email).length}
                      </p>
                    </div>
                    <ShieldCheck className="w-12 h-12 opacity-20 text-green-600" />
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-lg border-0">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">With Location</p>
                      <p className="text-3xl font-bold" style={{ color: 'rgb(26, 160, 157)' }}>
                        {users.filter(u => u.location).length}
                      </p>
                    </div>
                    <MapPin className="w-12 h-12 opacity-20" style={{ color: 'rgb(26, 160, 157)' }} />
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-lg border-0">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Without Location</p>
                      <p className="text-3xl font-bold text-amber-600">
                        {users.filter(u => !u.location).length}
                      </p>
                    </div>
                    <MapPin className="w-12 h-12 opacity-20 text-amber-600" />
                  </div>
                  {users.filter(u => !u.location).length > 0 && (
                    <Button onClick={handleEmailNoLocationUsers} data-testid="email-no-location-btn" disabled={emailSending} className="w-full mt-4 text-white" style={{ backgroundColor: 'rgb(26, 160, 157)' }} size="sm">
                      {emailSending ? (
                        <div className="flex items-center gap-2"><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>Sending...</div>
                      ) : (
                        <><Send className="w-4 h-4 mr-2" /> Email All to Set Location</>
                      )}
                    </Button>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Search */}
            <Card className="shadow-lg border-0 mb-6">
              <CardContent className="p-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input type="text" placeholder="Search by name or email..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
                </div>
              </CardContent>
            </Card>

            {/* Users List */}
            <Card className="shadow-lg border-0">
              <CardHeader>
                <CardTitle>Registered Users ({filteredUsers.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {filteredUsers.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No users found</p>
                ) : (
                  <div className="space-y-4">
                    {filteredUsers.map(u => (
                      <div key={u.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <h3 className="font-bold text-lg">{u.name}</h3>
                            {u.email === user?.email && <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">YOU</span>}
                            {getVerificationBadge(u)}
                          </div>
                          <div className="grid md:grid-cols-2 gap-2 text-sm text-gray-600">
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4" style={{ color: 'rgb(26, 160, 157)' }} />
                              <span>{u.email}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" style={{ color: 'rgb(26, 160, 157)' }} />
                              <span>Joined {formatDate(u.created_at)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4" style={{ color: u.location ? 'rgb(26, 160, 157)' : '#d1d5db' }} />
                              <span>{u.location ? `${u.location.address?.substring(0, 50)}...` : 'No location set'}</span>
                            </div>
                          </div>
                        </div>
                        {u.email !== user?.email && (
                          <Button onClick={() => handleDeleteUser(u.id, u.name, u.email)} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`delete-user-${u.id}`}>
                            <Trash2 className="w-4 h-4 mr-2" /> Delete User
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {activeTab === 'verifications' && (
          <Card className="shadow-lg border-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5" style={{ color: 'rgb(26, 160, 157)' }} />
                Pending ID Verifications ({pendingVerifications.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pendingVerifications.length === 0 ? (
                <div className="text-center py-12">
                  <ShieldCheck className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500 text-lg">No pending verifications</p>
                  <p className="text-gray-400 text-sm">All ID verification requests have been processed</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingVerifications.map(pv => (
                    <div key={pv.id} className="p-4 border-2 border-amber-200 bg-amber-50 rounded-lg">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="font-bold text-lg text-gray-900">{pv.name}</h3>
                          <p className="text-sm text-gray-600">{pv.email}</p>
                          <p className="text-xs text-gray-500 mt-1">Joined {formatDate(pv.created_at)}</p>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          {pv.id_image_url && (
                            <Button onClick={() => setViewIdImage(pv.id_image_url)} variant="outline" size="sm" data-testid={`view-id-${pv.id}`}>
                              <Eye className="w-4 h-4 mr-1" /> View ID
                            </Button>
                          )}
                          <Button onClick={() => handleVerifyUser(pv.id, 'verified')} size="sm" className="bg-green-600 hover:bg-green-700 text-white" data-testid={`approve-${pv.id}`}>
                            <Check className="w-4 h-4 mr-1" /> Approve
                          </Button>
                          <Button onClick={() => handleVerifyUser(pv.id, 'rejected')} size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`reject-${pv.id}`}>
                            <X className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
